<template>
  <div v-if="model?.isLoading" class="message-loader-container">
    <div class="message-loader-dot"></div>
    <div class="message-loader-text" data-cy="message-loader-text">
      {{ t('chat.answer.loadingMessage') }}
    </div>
  </div>
  <div
    v-if="!model?.isLoading"
    :class="model?.type === MessageType.Agent ? 'agent-message' : 'user-message'"
  >
    <Icon
      v-if="model?.type === MessageType.Agent"
      :model="
        appStore.currentMode() === DisplayMode.Light
          ? props.model?.avatar.light
          : props.model?.avatar.dark
      "
      :key="componentKey"
      :alt="props.model?.alt"
      class="agent-avatar"
    />

    <div class="message-content">
      <AttachmentsList
        v-if="props.model?.attachments?.length"
        :items="props.model?.attachments"
        mode="message"
        :showRemove="false"
        size="sm"
        align="right"
        class="attachments-block"
      />
      <div
        ref="messageRoot"
        :class="{
          'agent-text': model?.type === MessageType.Agent,
          'user-text': model?.type !== MessageType.Agent,
          'agent-text-error': model?.isError && model?.type === MessageType.Agent,
        }"
        :data-cy="
          model?.isError && model?.type === MessageType.Agent
            ? 'agent-text-error'
            : model?.type === MessageType.Agent
              ? 'agent-text'
              : 'user-text'
        "
        v-html="props.model?.text"
      ></div>
    </div>
    <Icon
      v-if="model?.type === MessageType.User"
      :model="
        appStore.currentMode() === DisplayMode.Light
          ? props.model?.avatar.light
          : props.model?.avatar.dark
      "
      :key="componentKey"
      :alt="props.model?.alt"
      class="user-avatar"
    />
  </div>

  <AgentResponseButtons
    v-if="!model?.isLoading && model?.type === MessageType.Agent"
    :model="props.model"
    :key="componentKey"
    @open-feedback-modal="handleOpenFeedbackModal($event)"
    @copy-agent-response="handleClipboardCopy"
    :copy-action-icon="
      appStore.currentMode() === DisplayMode.Light ? copyMessageIconLight : copyMessageIconDark
    "
  />
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { updateComponentKey } from '../../composables/ComponentKey'
import type { Message } from '../../types/chat/Message'
import { MessageType } from '../../types/chat/MessageType'
import Icon from '../Icon.vue'
import { t } from '../../composables/Translation'
import AgentResponseButtons from '../chat/AgentResponseButtons.vue'
import { FeedbackGrade } from '../../types/FeedbackGrade'
import { useClipboard } from '../../composables/UseMessageClipboard'
import { useCodeCopyButtons } from '../../composables/UseCodeCopyClipboard'
import AttachmentsList from '../AttachmentsList.vue'
import iconCopyLight from '../../assets/icons/light/icon-copy.svg'
import iconCopyDark from '../../assets/icons/dark/icon-copy.svg'
import iconCheckLight from '../../assets/icons/light/icon-check.svg'
import iconCheckDark from '../../assets/icons/dark/icon-check.svg'
import { formatPreSection } from '../../composables/ChatFormatting'
import { DisplayMode } from '../../types/app/displayMode'
import { useAppStore } from '../../stores/appStore'
import { useToast } from '../../composables/useToast'
import 'highlight.js/styles/github-dark.css'

const { copyRich } = useClipboard()
const toast = useToast()
const props = defineProps<{
  model: Message | undefined
}>()

const messageRoot = ref<HTMLElement | null>(null)
useCodeCopyButtons(messageRoot)
formatPreSection(messageRoot)

const copyMessageIconLight = ref<string>(iconCopyLight)
const copyMessageIconDark = ref<string>(iconCopyDark)
const appStore = useAppStore()

const emit = defineEmits<{
  (e: 'copy-agent-response'): void
  (e: 'open-feedback-modal', grade: FeedbackGrade): void
}>()

async function handleClipboardCopy() {
  try {
    await copyRich({ html: props.model?.text || '' })
    if (appStore.currentMode() === DisplayMode.Light) {
      copyMessageIconLight.value = iconCheckLight
      setTimeout(() => (copyMessageIconLight.value = iconCopyLight), 1200)
    } else {
      copyMessageIconDark.value = iconCheckDark
      setTimeout(() => (copyMessageIconDark.value = iconCopyDark), 1200)
    }
  } catch (err) {
    console.error(t('chat.copyError'), err)
    toast.error(`${t('chat.copyError')} ${err}`)
  }
}

function handleOpenFeedbackModal(grade: FeedbackGrade) {
  emit('open-feedback-modal', grade)
}

let componentKey = updateComponentKey(props)
</script>

<style scoped lang="scss">
:deep(.agent-avatar img) {
  width: 32px;
}

.agent-avatar {
  padding-top: 12px;
  align-self: stretch;
}

.agent-message {
  align-self: stretch;
  display: flex;
  width: 100%;
}

.message-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.attachments-block {
  margin-bottom: 4px;
  align-items: flex-end;
  display: flex;
}

.agent-text {
  padding: 12px;
  max-width: 724px;
}

.user-avatar {
  padding-left: 32px;
  width: 32px;
  aspect-ratio: 1/1;
  align-self: stretch;
}

.user-message {
  display: flex;
  padding: 0 0 24px 96px;
  align-items: flex-end;
  align-self: stretch;
}

.user-text {
  padding: 12px;
  width: 100%;
  border-radius: 18px 2px 18px 18px;
  background: var(--text-background-color);
}

.agent-text,
.user-text {
  color: var(--text-color);
  font-weight: 400;
  line-height: 150%;
}

.agent-text-error {
  color: #d93025;
  font-weight: 400;
  line-height: 150%;
}

.message-loader-container {
  display: flex;
  padding: 12px 0;
  align-items: center;
  gap: 10px;
  align-self: stretch;
}

.message-loader-dot {
  width: 16px;
  height: 16px;
  background-color: var(--primary-color);
  border-radius: 50%;
  animation: fade 1s infinite ease-in-out;
}

@keyframes fade {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.2;
  }
}

.message-loader-text {
  color: var(--text-color);
  font-weight: 700;
  line-height: 150%;
}

@media (max-width: 992px) {
  .user-message {
    padding: 0 0 24px 96px;
  }
  .user-text {
    width: 100%;
    padding: 12px 16px;
  }

  .agent-message {
    width: 100%;
    overflow: hidden;
    box-sizing: border-box;
  }

  .agent-avatar {
    float: none;
    width: unset;
    box-sizing: border-box;
  }

  .user-avatar {
    padding-left: 42px;
  }

  .agent-text {
    float: none;
    width: 100%;
    max-width: 948px;
    box-sizing: border-box;
    padding: 0px;
  }

  .agent-avatar + .agent-text {
    padding: 12px 32px 12px 12px;
  }

  .agent-message .message-content {
    overflow-x: auto;
  }

  div.agent-message:has(div.agent-avatar) {
    display: grid;
    grid-template-columns: 42px 1fr;
  }

  div.user-message:not(:has(div.user-avatar)) {
    padding-right: 32px;
  }
}
</style>
